/**
 * The rules engine's entire public surface.
 *
 * Exactly one implementation of cricket law exists, imported directly by the web
 * scorer, this backend, React Native in P1 and the CV service later (ADR-0001).
 * Two implementations means two behaviours means the scorer's phone and the public
 * link disagree about a result, which is the trust-destroying bug class the whole
 * design avoids.
 *
 * **Pure.** No database, no network, no clock, no randomness — the same events must
 * always produce the same state, which is what makes device/server agreement
 * guaranteed rather than hoped for, and what makes the YAML fixtures possible.
 *
 * **Nothing advisory is trusted.** `runs.total`, `legal`, `over`, `ballInOver`,
 * `freeHit` and the striker/non-striker pair are all recomputed here and the recorded
 * values ignored, because a stale one on a corrected event would otherwise put the
 * phone and the server a run apart (ADR-0002, and the field docs in `events.ts`).
 *
 * **Two-pass.** The ledger is first resolved into what actually applies — supersede
 * chains collapsed to their newest version *at the address of their oldest*, voided
 * events dropped — and only then folded (ADR-0004). One correction to ball 43 has to
 * re-derive every ball after it, so no other shape works.
 */
import type {
  BatterHand,
  BowlerChange,
  BowlingArm,
  BowlingType,
  Delivery,
  DismissalType,
  Event,
  InningsEndReason,
  NewOver,
  Wicket,
} from "./events.ts";
import { isBatterHand, isBowlingArm, isBowlingType, isDelivery } from "./events.ts";
import type { EventId, InningsNo, MatchId, PlayerId, TeamId, UserId } from "./ids.ts";
import type { RuleSet } from "./ruleset.ts";
import type {
  AnomalyRef,
  BattingLine,
  BowlingLine,
  DismissalFielder,
  DismissalSummary,
  EditMarker,
  ExtrasBreakdown,
  FallOfWicket,
  InningsState,
  LedgerPosition,
  MatchResult,
  MatchState,
  MatchStatus,
  OverCount,
  OverStripEntry,
  Partnership,
  RuleWarning,
  ScorerHeader,
} from "./state.ts";

// ── Effective ledger ────────────────────────────────────────────────────────

/**
 * One entry of the effective ledger: the version of an event that applies, and the
 * position it applies at.
 *
 * The pair is the whole point. A correction to delivery `seq` 43 is appended at
 * `seq` 56, and folding it at 56 would fix the total while leaving the twelve
 * deliveries in between credited to the wrong batter — corrected on its face and
 * internally inconsistent underneath (ADR-0004). So `event` is the newest version
 * and `address` is where it lands.
 */
export interface EffectiveEvent {
  /** The version that applies: the newest in this event's supersede chain. */
  readonly event: Event;
  /**
   * Where it applies — the address of the original, which for a correction is not
   * its own.
   */
  readonly address: LedgerPosition;
  /**
   * Every version of this event, oldest address first, ending in `event`.
   *
   * Carried because a correction can move a ball *away* from a participant: the
   * bowler who lost a reassigned over needs the edited marker on his line too, and
   * the only record that he ever had it is the version that got replaced.
   */
  readonly versions: readonly Event[];
  /** Non-null once this event has been corrected: who did it, when, how often. */
  readonly edited: EditMarker | null;
}

const addressOf = (event: Event): LedgerPosition => ({
  inningsNo: event.inningsNo,
  seq: event.seq,
});

/**
 * Ledger addresses order as `(inningsNo, seq)`.
 *
 * Never `seq` alone: it restarts at 1 in each innings, so a bare comparison sorts
 * the second innings' opening ball ahead of the first innings' 250th.
 */
const compareAddress = (a: LedgerPosition, b: LedgerPosition): number =>
  a.inningsNo - b.inningsNo || a.seq - b.seq;

/**
 * Resolve the ledger into what actually applies, in address order.
 *
 * Two rules, and both are load-bearing:
 *
 * 1. **A supersede chain resolves to its newest version, applied at the address of
 *    its oldest.** Chains of any depth collapse the same way — correct a ball, then
 *    correct the correction, and only the last version folds.
 * 2. **Voided events are skipped.** They stay in the ledger; they just never
 *    happened. Voiding is itself expressed by appending a superseding event with
 *    `voided: true`, so it is the *resolved* version whose flag decides — which also
 *    means a later correction can un-void a ball.
 *
 * Nothing here mutates or discards a ledger entry; the input is read-only and the
 * output is a fresh list of references into it.
 *
 * Deterministic regardless of the order events arrive in, which is what lets a
 * phone replaying its outbox and the server replaying its table agree byte for byte
 * (ADR-0001). Hence the sort up front, and "newest = highest address" rather than
 * "newest = last in the array".
 */
export function effectiveLedger(events: readonly Event[]): readonly EffectiveEvent[] {
  const ordered = [...events].sort(byAddressThenId);

  const byId = new Map<EventId, Event>();
  for (const event of ordered) {
    if (!byId.has(event.eventId)) byId.set(event.eventId, event);
  }

  // supersededId → the versions claiming to replace it. A list rather than one entry
  // because a chain forks whenever two corrections name the same original — a sheet that
  // always supersedes the ball rather than its latest version does it from one device.
  // Keeping only the winner here would not simplify anything: the loser would be
  // unclaimed below and fold a second time at its own address, double-counting the ball.
  const replacements = new Map<EventId, Event[]>();
  for (const event of ordered) {
    const target = event.supersedes;
    if (target === null || !byId.has(target)) continue;
    const existing = replacements.get(target);
    if (existing === undefined) replacements.set(target, [event]);
    else existing.push(event);
  }

  const claimed = new Set<EventId>();

  /** Walk one supersede chain from its original and collapse it to a single entry. */
  const resolve = (original: Event): EffectiveEvent | null => {
    const versions: Event[] = [];
    const pending: Event[] = [original];
    // `claimed` doubles as the cycle guard. A cycle cannot come from our own client —
    // `supersedes` names an event that already exists — but an unterminated walk here
    // would hang a scorer's phone mid-match, so it is guarded rather than assumed.
    while (pending.length > 0) {
      const version = pending.shift() as Event;
      if (claimed.has(version.eventId)) continue;
      claimed.add(version.eventId);
      versions.push(version);
      pending.push(...(replacements.get(version.eventId) ?? []));
    }
    versions.sort(byAddressThenId);

    const newest = versions[versions.length - 1] as Event;
    if (newest.voided) return null;

    const revisions = versions.length - 1;
    return {
      event: newest,
      address: addressOf(original),
      versions,
      edited: revisions === 0 ? null : editMarker(newest, revisions),
    };
  };

  const resolved: EffectiveEvent[] = [];

  for (const event of ordered) {
    if (claimed.has(event.eventId)) continue;
    // An original is an event that replaces nothing — or whose target has not synced
    // yet, in which case it stands alone at its own address until the original
    // arrives and the chain re-forms around it.
    if (event.supersedes !== null && byId.has(event.supersedes)) continue;
    const entry = resolve(event);
    if (entry !== null) resolved.push(entry);
  }

  // Anything left is reachable only through a supersede cycle. Folded at its own
  // address rather than dropped: losing a ball silently is the one outcome worse than
  // folding a malformed one (PRD §7.2 — a gap is a visible error, not a silent hole).
  for (const event of ordered) {
    if (claimed.has(event.eventId)) continue;
    const entry = resolve(event);
    if (entry !== null) resolved.push(entry);
  }

  return substituted(resolved.sort((a, b) => compareAddress(a.address, b.address)));
}

/**
 * Apply every `Correction` substitution — "this was actually a different player".
 *
 * Still pass one, which is the point. A substitution names a player, a replacement and the
 * `fromSeq` it takes effect at, so like a supersede it cannot be folded where it sits: it is
 * appended at the tail and applies from somewhere in the middle (ADR-0004). Resolving it
 * here keeps the fold ignorant of corrections entirely — `applyDelivery` never learns that a
 * substitution exists, it just sees a ledger with the right names in it.
 *
 * Done as a rewrite of participant ids rather than as a lens threaded through the fold,
 * because the ids are read in a dozen places — striker, non-striker, bowler, keeper, the
 * player out, every fielder — and a lens that missed one would credit a run to a player who
 * was never in the match. A rewrite cannot miss one silently: the field either goes through
 * `swap` or it does not compile.
 *
 * The rewritten events are copies. The originals stay exactly as appended, because nothing in
 * the ledger is ever mutated.
 */
function substituted(entries: readonly EffectiveEvent[]): readonly EffectiveEvent[] {
  const substitutions = entries.flatMap((entry) =>
    entry.event.type === "Correction" && entry.event.substitution !== null
      ? [{ inningsNo: entry.event.inningsNo, ...entry.event.substitution }]
      : [],
  );
  if (substitutions.length === 0) return entries;

  return entries.map((entry) => {
    // Which substitutions are live at this address. Applied in ledger order, so a player
    // substituted twice — A became B, then B became C — resolves to C rather than to
    // whichever the last pass happened to see.
    const live = substitutions.filter(
      (sub) => sub.inningsNo === entry.address.inningsNo && entry.address.seq >= sub.fromSeq,
    );
    if (live.length === 0) return entry;

    const swap = (playerId: PlayerId): PlayerId =>
      live.reduce((id, sub) => (id === sub.fromPlayerId ? sub.toPlayerId : id), playerId);

    const rewritten = rewriteParticipants(entry.event, swap);
    return rewritten === entry.event ? entry : { ...entry, event: rewritten };
  });
}

/** Every place an event names a player. Exhaustive by construction — see `substituted`. */
function rewriteParticipants(event: Event, swap: (playerId: PlayerId) => PlayerId): Event {
  if (isDelivery(event)) {
    const wicket = event.wicket;
    return {
      ...event,
      strikerId: swap(event.strikerId),
      nonStrikerId: swap(event.nonStrikerId),
      bowlerId: swap(event.bowlerId),
      keeperId: event.keeperId === null ? null : swap(event.keeperId),
      wicket:
        wicket === null
          ? null
          : {
              ...wicket,
              playerOutId: swap(wicket.playerOutId),
              fielderIds: wicket.fielderIds.map(swap),
            },
    };
  }

  switch (event.type) {
    case "BatterIn":
    case "BatterRetired":
      return { ...event, playerId: swap(event.playerId) };
    case "NewOver":
    case "BowlerChange":
      return { ...event, bowlerId: swap(event.bowlerId) };
    default:
      // No player to rewrite. `Correction` itself is in here deliberately: substituting the
      // subject of a substitution would let two of them chase each other.
      return event;
  }
}

function byAddressThenId(a: Event, b: Event): number {
  // The id tiebreak only matters for a malformed ledger with two events at one
  // address. It is here so the result is still the same on every device.
  return compareAddress(a, b) || (a.eventId < b.eventId ? -1 : a.eventId > b.eventId ? 1 : 0);
}

const editMarker = (correction: Event, revisions: number): EditMarker => ({
  by: correction.provenance.scorerId,
  at: correction.recordedAt,
  revisions,
});

/**
 * The match-level edited marker, read off the **raw** ledger.
 *
 * Not derived from the fold, because a voided delivery folds to nothing at all — it
 * has no chip to carry a marker, so a fold-derived answer would report a match as
 * unedited after a scorer deleted a ball from it. Every superseding version, every void and
 * every `Correction` is counted, and "who and when" comes from the latest of them.
 *
 * `Correction` is in that list even though it neither supersedes nor voids: it is the
 * cross-event amendment, and a substitution that renamed a player through half an innings
 * without the match saying it had been edited would be the loudest possible version of the
 * accusation this marker exists to defuse.
 */
function matchEditMarker(events: readonly Event[]): EditMarker | null {
  const corrections = events.filter(
    (event) => event.supersedes !== null || event.voided || event.type === "Correction",
  );
  if (corrections.length === 0) return null;
  const latest = corrections.reduce((a, b) => (byAddressThenId(a, b) >= 0 ? a : b));
  return editMarker(latest, corrections.length);
}

/** The correcting versions of an entry — every version but the original. */
const correctionsOf = (entry: EffectiveEvent): readonly Event[] => entry.versions.slice(1);

/**
 * The corrections that have touched one player's line.
 *
 * Keyed by correcting event so the same one counts once however many times it names the
 * player. It always names them more than once: a superseded delivery holds its bowler in
 * both versions, and a batter bowled off a ball he faced is both its striker and its
 * player out. Counting those would report two revisions for one edit.
 */
interface EditTrail {
  readonly counted: Set<EventId>;
  by: UserId | null;
  at: string;
  /** Address of the newest correction counted, so "who and when" is deterministic. */
  newest: LedgerPosition;
}

const markerFor = (
  trails: Map<PlayerId, EditTrail>,
  playerId: PlayerId,
): EditMarker | null => {
  const trail = trails.get(playerId);
  return trail === undefined
    ? null
    : { by: trail.by, at: trail.at, revisions: trail.counted.size };
};

function noteEdits(
  trails: Map<PlayerId, EditTrail>,
  playerId: PlayerId,
  corrections: readonly Event[],
): void {
  for (const correction of corrections) {
    const at = addressOf(correction);
    const existing = trails.get(playerId);
    if (existing === undefined) {
      trails.set(playerId, {
        counted: new Set([correction.eventId]),
        by: correction.provenance.scorerId,
        at: correction.recordedAt,
        newest: at,
      });
      continue;
    }
    existing.counted.add(correction.eventId);
    if (compareAddress(at, existing.newest) >= 0) {
      existing.by = correction.provenance.scorerId;
      existing.at = correction.recordedAt;
      existing.newest = at;
    }
  }
}

/**
 * Mark the bowlers a corrected `NewOver` or `BowlerChange` names.
 *
 * Reassigning an over supersedes the event that named the bowler as well as every ball
 * in it, so the chain's versions hold both the bowler who lost the over and the one
 * who gained it.
 */
function noteBowlingStyle(bowler: BowlerAcc, event: NewOver | BowlerChange): void {
  // Each is taken on its own: an event that names only the type leaves the arm as it was, and a
  // value that is not one of the allowed ones is ignored rather than stored (ADR-0011).
  if (isBowlingArm(event.bowlingArm)) bowler.arm = event.bowlingArm;
  if (isBowlingType(event.bowlingType)) bowler.type = event.bowlingType;
}

function noteBowlerEdit(acc: InningsAcc, entry: EffectiveEvent): void {
  const corrections = correctionsOf(entry);
  if (corrections.length === 0) return;
  for (const version of entry.versions) {
    if (version.type === "NewOver" || version.type === "BowlerChange") {
      noteEdits(acc.bowlingEdits, version.bowlerId, corrections);
    }
  }
  acc.lastCorrections = corrections;
}

// ── Cricket arithmetic ──────────────────────────────────────────────────────

/**
 * Whether this delivery counts towards the over.
 *
 * Derived from the extras, never read off `delivery.legal`. Trusting the recorded
 * flag would let a wide advance the over — and now that corrections land as
 * superseding events, one that changed the extras but left the flag stale would
 * silently shorten or lengthen an over for the rest of the innings.
 */
const isLegalDelivery = (delivery: Delivery): boolean =>
  delivery.runs.extras.wide === 0 && delivery.runs.extras.noball === 0;

/**
 * How many times the batters physically crossed. This — not the run total — is what
 * decides strike.
 *
 * A boundary means the ball reached the fence, so nobody ran. A wide's first run is
 * the penalty and was not run: `wide: 3` is one penalty plus two actually run, so the
 * batters keep their ends, while `wide: 2` is one run and they swap. That second case
 * is one a naive "extras never rotate strike" implementation misses entirely.
 */
function crossings(delivery: Delivery): number {
  const { runs } = delivery;
  const { extras } = runs;

  if (runs.boundary !== null && !runs.overthrow) return 0;

  const batterRunsRun =
    runs.boundary !== null && runs.overthrow ? Math.max(0, runs.batter - 4) : runs.batter;
  const wideRunsRun = extras.wide > 0 ? extras.wide - 1 : 0;

  return batterRunsRun + extras.bye + extras.legbye + wideRunsRun;
}

/**
 * Runs credited to the striker, after any short run is taken off.
 *
 * A run that is short is not scored. `Runs.batter` is what they *ran*, so the deduction
 * has to come off the striker's credit as well as off the team total — `teamRuns` below
 * already takes it off the total, and deducting in only one of the two places leaves the
 * batting card and the innings total disagreeing by the short run. A scorecard that does
 * not add up is the fastest way there is to lose a scorer's trust.
 *
 * Note what is deliberately *not* adjusted: `crossings()`. A short run still moved the
 * batters — the umpire's signal says the ground was not made good, not that the batter is
 * standing somewhere else — so three runs with one short still rotates strike while
 * scoring two. That combination is exactly where a naive implementation puts the wrong
 * batter on strike for the rest of the over.
 */
function creditedBatterRuns(delivery: Delivery): number {
  return Math.max(0, delivery.runs.batter - delivery.runs.shortRun);
}

/** Runs charged against the bowler. Byes and leg byes are the team's, not his. */
function runsChargedToBowler(delivery: Delivery): number {
  const { extras } = delivery.runs;
  return creditedBatterRuns(delivery) + extras.wide + extras.noball;
}

/** Runs the batting side receives from this delivery. */
function teamRuns(delivery: Delivery): number {
  const { extras } = delivery.runs;
  return (
    delivery.runs.batter +
    extras.wide +
    extras.noball +
    extras.bye +
    extras.legbye +
    extras.penalty -
    delivery.runs.shortRun
  );
}

/** Dismissals credited to the bowler. A run out is the fielders', not his. */
const WICKETS_TO_BOWLER = new Set<Wicket["type"]>([
  "bowled",
  "caught",
  "caught_behind",
  "caught_and_bowled",
  "lbw",
  "stumped",
  "hit_wicket",
]);

/**
 * Dismissals that can only be the batter on strike.
 *
 * Not part of `impossibleDismissalReason` below, and the difference is the point: that
 * predicate is pure over one delivery's own facts, so the scoring screen can call it at
 * entry. This one asks who was *facing*, which is derived from every ball before it — a
 * question no pad can answer differently, since at entry the app's striker is by
 * construction the one it just recorded. Only a correction can pull the two apart.
 *
 * Deliberately a separate set from `WICKETS_TO_BOWLER` despite the same membership
 * today: one is about credit and the other about who was facing, and a RuleSet that
 * changed either has no business moving the other. `run_out`, `mankad`,
 * `obstructing_the_field` and `timed_out` are absent because either batter can be the
 * one out.
 */
const STRIKER_ONLY_DISMISSALS = new Set<DismissalType>([
  "bowled",
  "caught",
  "caught_behind",
  "caught_and_bowled",
  "lbw",
  "stumped",
  "hit_wicket",
]);

/** Dismissals that cannot happen off a no-ball, or on a free hit (PRD §6.4). */
const IMPOSSIBLE_OFF_A_NO_BALL = new Set<DismissalType>([
  "caught",
  "caught_behind",
  "caught_and_bowled",
  "stumped",
  "bowled",
  "lbw",
  "hit_wicket",
]);

/** The only dismissals possible off a wide: the batter never legally faced the ball. */
const POSSIBLE_OFF_A_WIDE = new Set<DismissalType>(["stumped", "run_out", "mankad"]);

/**
 * A dismissal type as a scorer says it, not as the enum spells it.
 *
 * The enum is snake_case; every string built from it is read by a human — greyed out on
 * the pad, listed as an anomaly on the correction sheet — and "caught_behind is not
 * possible…" leaks the schema into the product.
 */
const spokenDismissal = (type: DismissalType): string => type.replace(/_/g, " ");

/** What a delivery says about which dismissals were available on it. */
export interface DismissalContext {
  readonly wide: boolean;
  readonly noBall: boolean;
  /** True when this delivery *is* a free hit, not when it causes one. */
  readonly freeHit: boolean;
}

/**
 * Why this dismissal was impossible on this delivery, or null if it was possible.
 *
 * Exported, because ADR-0011 puts enforcement at *entry*: the scoring screen greys
 * out `stumped` the moment `NB` is latched and shows this string as the reason,
 * while the fold below calls the same function to flag a delivery that arrived
 * illegal anyway. A second copy of these lists in the UI would be a second
 * implementation of cricket law (ADR-0001) — and the failure mode is specific and
 * nasty: the client offers a dismissal the server then flags as an anomaly, so the
 * scorer is blocked from completing a match over a ball the UI told him was fine.
 *
 * This is not a second traversal of the ledger (ADR-0006). It is pure over one
 * delivery's own facts, and the caller supplies the context the fold already knows.
 */
export function impossibleDismissalReason(
  type: DismissalType,
  context: DismissalContext,
  ruleSet: RuleSet,
): string | null {
  const spoken = spokenDismissal(type);
  // Retiring out happens *between* deliveries. Recorded on one it would additionally
  // credit a ball faced to the batter and a legal ball to the bowler, so it is a
  // `BatterRetired { reason: "out" }` and never a `Delivery.wicket` (see events.ts).
  if (type === "retired_out") {
    return "retired out is recorded between deliveries, not on one";
  }
  if (type === "lbw" && !ruleSet.lbwEnabled) {
    return "this RuleSet does not play lbw";
  }
  if (context.noBall && IMPOSSIBLE_OFF_A_NO_BALL.has(type)) {
    return `${spoken} is not possible off a no-ball`;
  }
  // The protection a free hit grants is the same protection a no-ball grants, which
  // is why it reuses the same set rather than a parallel one that could drift.
  if (context.freeHit && IMPOSSIBLE_OFF_A_NO_BALL.has(type)) {
    return `${spoken} is not possible on a free hit`;
  }
  if (context.wide && !POSSIBLE_OFF_A_WIDE.has(type)) {
    return `${spoken} is not possible off a wide`;
  }
  return null;
}

const wicketsThatEndInnings = (ruleSet: RuleSet): number =>
  ruleSet.maxWickets ?? (ruleSet.lastManStands ? ruleSet.playersPerSide : ruleSet.playersPerSide - 1);

/**
 * How many wickets end *this* innings, given who is off the field.
 *
 * An innings ends when the batting side cannot put two batters at the crease — normally
 * at ten wickets, because the eleventh man has nobody to partner. A batter who retired
 * hurt and has not come back is one fewer body, so the innings ends a wicket earlier: nine
 * down with a man in hospital is all out.
 *
 * Getting this wrong is not a cosmetic bug. Too high and the innings never terminates, so
 * the scorer is stuck on a screen demanding a batter who does not exist; too low and a
 * side is declared all out with men still to bat.
 *
 * It self-corrects, which is why it is derived here rather than latched: the moment the
 * retired batter returns his marker clears and the ceiling goes back up.
 *
 * Deliberately *not* used for the winning margin. "Won by 5 wickets" counts wickets that
 * fell against the format's ten, which is the convention every scorecard in the sport
 * uses, and a retirement does not change how a margin is written.
 */
function wicketsToEndThisInnings(ruleSet: RuleSet, acc: InningsAcc): number {
  let awayFromTheCrease = 0;
  for (const batter of acc.batters.values()) {
    // `retired` is a *current* status, cleared when a batter resumes — so this counts
    // only those still off the field, and never a batter who came back.
    if (batter.retired === "hurt" || batter.retired === "not_out") awayFromTheCrease += 1;
  }
  // Never below one: a side is not "all out" before a single wicket has fallen, however
  // many batters are unavailable.
  return Math.max(1, wicketsThatEndInnings(ruleSet) - awayFromTheCrease);
}

const overCount = (legalBalls: number, ballsPerOver: number): OverCount => ({
  completed: Math.floor(legalBalls / ballsPerOver),
  balls: legalBalls % ballsPerOver,
});

// ── Mutable accumulators ────────────────────────────────────────────────────

interface BatterAcc {
  playerId: PlayerId;
  battingOrder: number;
  batted: boolean;
  runs: number;
  balls: number;
  fours: number;
  sixes: number;
  dots: number;
  dismissal: DismissalSummary | null;
  retired: "hurt" | "out" | "not_out" | null;
  /** From the `BatterIn` that brought him in; null until one says (ADR-0023). */
  hand: BatterHand | null;
}

interface BowlerAcc {
  playerId: PlayerId;
  /** From the latest `NewOver` or `BowlerChange` that named them; null until one does (ADR-0023). */
  arm: BowlingArm | null;
  type: BowlingType | null;
  legalBalls: number;
  runsConceded: number;
  wickets: number;
  wides: number;
  noBalls: number;
  dots: number;
  maidens: number;
  wicketMaidens: number;
}

interface PartnershipAcc {
  runs: number;
  balls: number;
  a: PlayerId;
  b: PlayerId;
  unbroken: boolean;
}

interface InningsAcc {
  inningsNo: InningsNo;
  battingTeamId: TeamId;
  bowlingTeamId: TeamId;
  oversAvailable: number | null;
  target: number | null;
  isSuperOver: boolean;
  followOn: boolean;

  runs: number;
  wickets: number;
  legalBalls: number;
  extras: { wide: number; noball: number; bye: number; legbye: number; penalty: number };

  batters: Map<string, BatterAcc>;
  battingOrderNext: number;
  bowlers: Map<string, BowlerAcc>;

  striker: PlayerId | null;
  nonStriker: PlayerId | null;
  currentBowler: PlayerId | null;
  previousOverBowler: PlayerId | null;

  /** Reset at each over boundary, for maiden detection. */
  overRunsCharged: number;
  overBowlerWickets: number;

  freeHit: boolean;
  interrupted: boolean;
  ended: InningsEndReason | null;

  fallOfWickets: FallOfWicket[];
  partnerships: PartnershipAcc[];
  deliveries: OverStripEntry[];
  /**
   * Penalty runs awarded to this innings' total before it started.
   *
   * A five-run penalty against the fielding side is credited to *their* batting innings
   * (`creditToInningsNo`), which in the first innings has not begun yet. Without a
   * holding place the runs would land on an accumulator that does not exist and be
   * silently lost — a ten-run swing, since the same five are also not on the other side.
   */
  penaltyBefore: number;
  /**
   * True once an `OversRevised` has cut this innings' allocation.
   *
   * Gates the par score. A par score is only meaningful once the chase has been shortened
   * — quoting one in an untouched innings is just the run rate wearing a more official hat,
   * and it invites the reader to think a rain rule is in play when none is.
   */
  oversWereRevised: boolean;

  /**
   * Which players' lines a correction touched.
   *
   * Kept as a side-map rather than written onto `BatterAcc`/`BowlerAcc` because a
   * correction can name a player who has no line in the corrected innings at all —
   * the bowler whose over was reassigned away. Creating one for him would put a
   * phantom `0-0-0-0` on the card.
   */
  battingEdits: Map<PlayerId, EditTrail>;
  bowlingEdits: Map<PlayerId, EditTrail>;
  /**
   * The corrections carried by the most recently folded corrected event.
   *
   * What a re-attributed delivery is blamed on. A ball can move to the other batter
   * without being corrected itself — that is ADR-0004's whole example, twelve deliveries
   * re-credited by one edit to seq 43 — and the correction responsible is upstream of
   * it, so the fold has to remember one.
   */
  lastCorrections: readonly Event[];
}

// ── The fold ────────────────────────────────────────────────────────────────

export function replay(
  events: readonly Event[],
  ruleSet: RuleSet,
  matchId: MatchId,
): MatchState {
  const ledger = effectiveLedger(events);
  const innings: InningsAcc[] = [];
  /**
   * Penalties credited to an innings that has not started yet, keyed by innings number.
   *
   * Five runs against the fielding side in the first innings belong to their own batting
   * innings, which begins later. Holding them here rather than dropping them is the
   * difference between a ten-run swing and a correct scorecard.
   */
  const pendingPenalties = new Map<InningsNo, number>();
  const warnings: RuleWarning[] = [];
  const anomalies: AnomalyRef[] = [];
  let status: MatchStatus = "live";

  const currentInnings = (): InningsAcc | undefined => innings[innings.length - 1];

  /**
   * Flag a delivery the engine considers cricket-illegal. Accepted anyway (ADR-0011).
   *
   * Addressed by where the delivery *applies*, not by the correcting event's own
   * `seq`, because the client resolves an anomaly by finding the chip at that address
   * — and after a correction those two numbers differ.
   */
  const flag = (entry: EffectiveEvent, message: string): void => {
    anomalies.push({
      eventId: entry.event.eventId,
      inningsNo: entry.address.inningsNo,
      seq: entry.address.seq,
      message,
    });
  };

  const batterOf = (acc: InningsAcc, playerId: PlayerId): BatterAcc => {
    const existing = acc.batters.get(playerId);
    if (existing) return existing;
    const created: BatterAcc = {
      playerId,
      battingOrder: acc.battingOrderNext++,
      batted: false,
      runs: 0,
      balls: 0,
      fours: 0,
      sixes: 0,
      dots: 0,
      dismissal: null,
      retired: null,
      hand: null,
    };
    acc.batters.set(playerId, created);
    return created;
  };

  const bowlerOf = (acc: InningsAcc, playerId: PlayerId): BowlerAcc => {
    const existing = acc.bowlers.get(playerId);
    if (existing) return existing;
    const created: BowlerAcc = {
      playerId,
      arm: null,
      type: null,
      legalBalls: 0,
      runsConceded: 0,
      wickets: 0,
      wides: 0,
      noBalls: 0,
      dots: 0,
      maidens: 0,
      wicketMaidens: 0,
    };
    acc.bowlers.set(playerId, created);
    return created;
  };

  const swapEnds = (acc: InningsAcc): void => {
    const striker = acc.striker;
    acc.striker = acc.nonStriker;
    acc.nonStriker = striker;
  };

  const openPartnership = (acc: InningsAcc): void => {
    if (acc.striker === null || acc.nonStriker === null) return;
    acc.partnerships.push({
      runs: 0,
      balls: 0,
      a: acc.striker,
      b: acc.nonStriker,
      unbroken: true,
    });
  };

  /**
   * Order matters: the target is checked before all-out, because a side that hits the
   * winning run off the same ball as its last wicket has *won*. Checking all-out first
   * renders the winners as "6 all out" and the result as "won by 0 wickets".
   */
  const checkTermination = (acc: InningsAcc): void => {
    if (acc.ended !== null) return;
    if (acc.target !== null && acc.runs >= acc.target) {
      acc.ended = "target_reached";
      return;
    }
    if (acc.wickets >= wicketsToEndThisInnings(ruleSet, acc)) {
      acc.ended = "all_out";
      return;
    }
    const available = acc.oversAvailable ?? ruleSet.overs;
    if (available !== null && acc.legalBalls >= available * ruleSet.ballsPerOver) {
      acc.ended = "overs_complete";
    }
  };

  /**
   * Bowler restrictions, surfaced and never enforced (PRD §6.4).
   *
   * `overriddenReason` is carried through from the event so a warning the scorer
   * consciously accepted is distinguishable from one nobody answered for.
   */
  const checkBowlerLimits = (
    acc: InningsAcc,
    bowlerId: PlayerId,
    isNewOver: boolean,
    overriddenReason: string | null,
  ): void => {
    if (
      isNewOver &&
      !ruleSet.consecutiveOversAllowed &&
      acc.previousOverBowler !== null &&
      acc.previousOverBowler === bowlerId
    ) {
      warnings.push({
        code: "consecutive_overs",
        message: "This bowler bowled the previous over, which this RuleSet disallows",
        playerId: bowlerId,
        overriddenReason,
      });
    }
    const bowler = bowlerOf(acc, bowlerId);
    if (ruleSet.maxOversPerBowler !== null) {
      const completed = Math.floor(bowler.legalBalls / ruleSet.ballsPerOver);
      if (completed >= ruleSet.maxOversPerBowler) {
        warnings.push({
          code: "max_overs_per_bowler",
          message: `This bowler has already bowled ${completed} of ${ruleSet.maxOversPerBowler} permitted overs`,
          playerId: bowlerId,
          overriddenReason,
        });
      }
    }
  };

  function applyDelivery(acc: InningsAcc, entry: EffectiveEvent, delivery: Delivery): void {
    const { runs } = delivery;
    const { extras } = runs;
    const legal = isLegalDelivery(delivery);
    // Where this ball's anomalies will start, so the chip can carry its own. Captured by
    // position rather than filtered by `eventId` afterwards, because a correction and the
    // ball it replaces can share neither — the id on the chip is the newest version's.
    const anomaliesBefore = anomalies.length;

    // Who *faced* this ball is derived, not read off the event.
    //
    // Strike is the one participant a correction can move retrospectively, because it
    // depends on every prior ball: fix seq 43 from an even score to an odd one and the
    // twelve deliveries after it were faced by the other batter, as a matter of cricket
    // fact, whatever the scorer's device believed at the time. Crediting
    // `delivery.strikerId` would fix the total and leave twelve balls and their runs on
    // the wrong batting line forever — the corrected-on-its-face, inconsistent-
    // underneath scorecard ADR-0004 exists to prevent. So `strikerId` is advisory, for
    // the same reason `runs.total` is.
    //
    // The bowler is not like this. Who bowled is an independent observation with no
    // dependency on earlier balls, so `delivery.bowlerId` is authoritative — and
    // superseding it ball by ball is exactly how an over gets reassigned (CONTEXT.md).
    //
    // **A correcting delivery is the exception, and it is the whole of the retrospective
    // strike swap (#15).** On an *original* delivery the recorded pair is redundant — it is
    // whatever the app derived at the time — so ignoring it costs nothing. On a *correcting*
    // one it is what a human deliberately typed while looking at that ball, which makes it
    // the same kind of fact as `BatterIn.takesStrike`: the scorer's own account of who was
    // where, and human authority wins (PRD §4.9).
    //
    // So a correction re-anchors the ends at its address and every ball after it re-derives
    // from there. That is what makes "the batters were the other way round from here"
    // expressible at all — without it the swap has to be chased back to whichever earlier
    // ball had its crossings mis-recorded, which is not something a scorer can be asked to
    // find on a ground.
    //
    // A no-op for the case that matters most. Reassigning an over copies each ball and
    // changes only the bowler, so the pair carried is the pair the fold had already derived
    // and re-anchoring lands on the same two players.
    //
    // The recorded pair is also the fallback for the one thing the fold cannot derive at
    // all: who is in, when no `BatterIn` has said so.
    if (entry.edited !== null) {
      acc.striker = delivery.strikerId;
      acc.nonStriker = delivery.nonStrikerId;
    } else if (acc.striker === null && acc.nonStriker === null) {
      acc.striker = delivery.strikerId;
      acc.nonStriker = delivery.nonStrikerId;
    } else if (acc.striker === null || acc.nonStriker === null) {
      // When lastManStands and all partners are dismissed, the surviving batter faces
      // alone — the empty slot is genuinely empty, not waiting for a `BatterIn`. The
      // survivor goes to the striker's end because there is nobody to bat at the other.
      const lastManAlone = ruleSet.lastManStands &&
        acc.wickets >= wicketsThatEndInnings(ruleSet) - 1;
      if (lastManAlone) {
        if (acc.striker === null && acc.nonStriker !== null) {
          acc.striker = acc.nonStriker;
          acc.nonStriker = null;
        }
      } else {
        // A wicket fell and no `BatterIn` followed it. The survivor's end *was* derived —
        // for a run out it is the only thing that knows which end he finished at — so only
        // the empty end is filled, from whichever of the recorded pair is not him. Taking
        // the recorded pair wholesale would throw the survivor's end away, and filling both
        // ends from it can put one player at both and credit him twice.
        const survivor = acc.striker ?? acc.nonStriker;
        const incoming =
          delivery.strikerId === survivor ? delivery.nonStrikerId : delivery.strikerId;
        if (acc.striker === null) acc.striker = incoming;
        else acc.nonStriker = incoming;
      }
    }
    const strikerId = acc.striker as PlayerId;
    const nonStrikerId = acc.nonStriker;

    const striker = batterOf(acc, strikerId);
    const bowler = bowlerOf(acc, delivery.bowlerId);

    striker.batted = true;
    if (nonStrikerId !== null) batterOf(acc, nonStrikerId).batted = true;

    // Captured before anything increments, because the chip's position is "which
    // delivery attempt of which over was this" — and a wide bowled after five legal
    // balls belongs to over 0 ball 6, not to the next over.
    const legalBallsBefore = acc.legalBalls;

    // ── Runs ────────────────────────────────────────────────────────────────
    const gained = teamRuns(delivery);
    acc.runs += gained;
    acc.extras.wide += extras.wide;
    acc.extras.noball += extras.noball;
    acc.extras.bye += extras.bye;
    acc.extras.legbye += extras.legbye;
    acc.extras.penalty += extras.penalty;

    const credited = creditedBatterRuns(delivery);
    striker.runs += credited;
    // A boundary only belongs to the batter if the runs were credited to him. A wide
    // that reaches the fence is five wides and nothing else — crediting it as the
    // batter's four inflates his boundary count and strike rate for a ball he never
    // touched.
    if (runs.boundary === "four" && credited >= 4) striker.fours += 1;
    if (runs.boundary === "six" && credited >= 6) striker.sixes += 1;

    // A wide is not a ball faced; a no-ball is.
    if (extras.wide === 0) striker.balls += 1;

    const charged = runsChargedToBowler(delivery);
    bowler.runsConceded += charged;
    bowler.wides += extras.wide;
    bowler.noBalls += extras.noball;

    if (legal) {
      acc.legalBalls += 1;
      bowler.legalBalls += 1;
      // A wicket-taking ball off which nothing was scored is still a dot.
      if (gained === 0) {
        striker.dots += 1;
        bowler.dots += 1;
      }
    }

    acc.overRunsCharged += ruleSet.byesBreakMaiden ? gained : charged;

    const openPartner = acc.partnerships[acc.partnerships.length - 1];
    if (openPartner?.unbroken === true) {
      openPartner.runs += gained;
      if (extras.wide === 0) openPartner.balls += 1;
    }

    // ── Free hit ────────────────────────────────────────────────────────────
    // Set by a no-ball where the RuleSet enables it, and carried until the next
    // *legal* delivery consumes it — so a wide in between does not clear it.
    const wasFreeHit = acc.freeHit;
    if (extras.noball > 0 && ruleSet.noBallFreeHit) {
      acc.freeHit = true;
    } else if (legal && wasFreeHit) {
      acc.freeHit = false;
    }

    // ── Wicket ──────────────────────────────────────────────────────────────
    let dismissed: PlayerId | null = null;
    if (delivery.wicket !== null) {
      const wicket = delivery.wicket;
      dismissed = wicket.playerOutId;
      batterOf(acc, wicket.playerOutId).dismissal = summariseDismissal(wicket, delivery);
      acc.wickets += 1;
      const creditedToBowler = WICKETS_TO_BOWLER.has(wicket.type);
      if (creditedToBowler) {
        bowler.wickets += 1;
        acc.overBowlerWickets += 1;
      }

      // PRD §6.4 — off a no-ball only run out, obstructing and hit-ball-twice are
      // possible. Flagged rather than rejected: the client prevents this at entry, so
      // one arriving here means version skew, and destroying a scorer's work over our
      // own deploy bug is indefensible (ADR-0011). Unresolved anomalies block match
      // completion, which is what stops a silently wrong scorecard.
      const impossible = impossibleDismissalReason(
        wicket.type,
        { wide: extras.wide > 0, noBall: extras.noball > 0, freeHit: wasFreeHit },
        ruleSet,
      );
      if (impossible !== null) flag(entry, impossible);

      // The ledger and the derived rotation disagree about who was facing. The way this
      // arises is a correction upstream that re-rotated strike: the batter recorded here
      // is the one who physically walked off, so either the correction is wrong or the
      // balls in between are, and only a human can say which. Not guessed at — deriving
      // the player out would put a batter back on a field he has left — and not rejected
      // either, because an unresolved anomaly blocking completion is what gets a human to
      // look (ADR-0011).
      if (STRIKER_ONLY_DISMISSALS.has(wicket.type) && wicket.playerOutId !== strikerId) {
        flag(entry, `${spokenDismissal(wicket.type)} names a batter who was not on strike`);
      }

      acc.fallOfWickets.push({
        wicketNo: acc.wickets,
        runs: acc.runs,
        playerOutId: wicket.playerOutId,
        overs: overCount(acc.legalBalls, ruleSet.ballsPerOver),
      });
      if (openPartner?.unbroken === true) openPartner.unbroken = false;
    }

    // ── Over strip ──────────────────────────────────────────────────────────
    acc.deliveries.push({
      // The id of the version that applies, so the correction sheet this chip opens
      // supersedes the newest one; the address of the original, because that is where
      // in the innings the ball sits (ADR-0004).
      eventId: delivery.eventId,
      seq: entry.address.seq,
      over: Math.floor(legalBallsBefore / ruleSet.ballsPerOver),
      ballInOver: (legalBallsBefore % ruleSet.ballsPerOver) + 1,
      legal,
      // Copied, not aliased — the state must not share structure with the input.
      runs: { batter: runs.batter, extras: { ...extras }, total: gained },
      wicket: delivery.wicket !== null,
      freeHit: wasFreeHit,
      batterId: strikerId,
      batterHand: acc.batters.get(strikerId)?.hand ?? null,
      shot: delivery.shot === null ? null : { ...delivery.shot },
      shortRun: runs.shortRun,
      edited: entry.edited,
      // On the chip, because the chip is what the scorer taps to resolve it (ADR-0011:
      // unresolved anomalies block completion). Reading `state.anomalies` and joining it
      // against chips by id in the view would be the second traversal ADR-0006 forbids —
      // and the join would be wrong anyway, since an anomaly is addressed by where the ball
      // applies while the chip carries the newest version's id.
      //
      // Joined rather than kept as a list: one ball can be illegal in two ways at once — a
      // stumping off a no-ball that also names a batter who was not on strike — and a
      // scorer needs both reasons, not the first one.
      anomaly:
        anomalies.length === anomaliesBefore
          ? null
          : anomalies
              .slice(anomaliesBefore)
              .map((flagged) => flagged.message)
              .join("; "),
      source: delivery.provenance.source,
      confidence: delivery.provenance.confidence,
    });

    // ── Strike ──────────────────────────────────────────────────────────────
    // Order is load-bearing. Runs first, then the end change — so an odd run off the
    // last ball of the over swaps twice and nets out to no swap, which the PRD names
    // as where naive implementations break.
    const isRunOut = delivery.wicket?.type === "run_out" || delivery.wicket?.type === "mankad";
    const crossed = isRunOut
      ? (delivery.wicket?.runsCompletedBefore ?? 0)
      : crossings(delivery);
    // When the last man bats alone, odd crossings cannot move him off strike — there
    // is nobody at the other end to face.
    if (crossed % 2 === 1 && acc.nonStriker !== null) swapEnds(acc);

    if (dismissed !== null && delivery.wicket !== null) {
      positionSurvivor(acc, delivery.wicket, dismissed);
    }

    // Over completion is derived from the legal-ball count, never from a counter that
    // a stray mid-over `NewOver` could reset.
    if (legal && acc.legalBalls % ruleSet.ballsPerOver === 0) {
      if (acc.overRunsCharged === 0) {
        bowler.maidens += 1;
        // A wicket-maiden needs a wicket credited to *him* — a run out in a maiden
        // over is not the bowler's.
        if (acc.overBowlerWickets > 0) bowler.wicketMaidens += 1;
      }
      acc.previousOverBowler = acc.currentBowler;
      acc.overRunsCharged = 0;
      acc.overBowlerWickets = 0;
      if (acc.nonStriker !== null) swapEnds(acc);
    }

    // ── Edited markers ──────────────────────────────────────────────────────
    // Marked on every line whose numbers a correction could have moved. A card that
    // changed while showing no sign of it is the accusation ADR-0004's marker exists to
    // defuse, so the rule is inclusive: the batter who faced the ball, the batter it
    // dismissed, and the bowler named in *every* version — reassigning an over takes the
    // figures off whoever was credited with them, and his line has to say so.
    const corrections = correctionsOf(entry);
    if (corrections.length > 0) {
      // *Both* ends, and both ends of every version. A correction re-anchors the pair at
      // this address (#15), so it can hand the ball from one batter to the other — and the
      // one who lost it is the line a reader will query first. Marking only the batter who
      // ends up facing would leave his total quietly reduced and unmarked.
      noteEdits(acc.battingEdits, strikerId, corrections);
      if (nonStrikerId !== null) noteEdits(acc.battingEdits, nonStrikerId, corrections);
      if (dismissed !== null) noteEdits(acc.battingEdits, dismissed, corrections);
      for (const version of entry.versions) {
        if (!isDelivery(version)) continue;
        // Reassigning an over takes the figures off whoever was credited with them, and his
        // line has to say so.
        noteEdits(acc.bowlingEdits, version.bowlerId, corrections);
        noteEdits(acc.battingEdits, version.strikerId, corrections);
        noteEdits(acc.battingEdits, version.nonStrikerId, corrections);
      }
      acc.lastCorrections = corrections;
    } else if (delivery.strikerId !== strikerId && acc.lastCorrections.length > 0) {
      // This ball was not corrected and still moved: an edit upstream re-rotated strike,
      // so it is now credited to the other batter. Both lines are marked, the one that
      // gained the ball and the one that lost it — the batter whose total dropped is the
      // one a reader will query, and his line is the one the fold would otherwise leave
      // looking untouched.
      noteEdits(acc.battingEdits, strikerId, acc.lastCorrections);
      noteEdits(acc.battingEdits, delivery.strikerId, acc.lastCorrections);
    }
  }

  /**
   * Place the surviving batter after a wicket, so the next ball's strike is right.
   *
   * `Wicket.end` is where the dismissal happened, which is where the batter who is out
   * was standing — so the survivor is at the other end. For a run out that is the only
   * way to know, since the batters may have crossed any number of times.
   *
   * The empty slot is then filled by the following `BatterIn`, whose `takesStrike` the
   * scorer confirms and which therefore wins (PRD §4.9, human authority).
   */
  function positionSurvivor(acc: InningsAcc, wicket: Wicket, dismissed: PlayerId): void {
    const survivor = acc.striker === dismissed ? acc.nonStriker : acc.striker;

    // Under the pre-2017 rule, a catch taken after the batters crossed leaves the
    // survivor at the striker's end and he faces the next ball. Under current Law the
    // new batter takes strike regardless, which is the default.
    const catchCrossedRule =
      !ruleSet.newBatterTakesStrikeOnCaught &&
      (wicket.type === "caught" ||
        wicket.type === "caught_behind" ||
        wicket.type === "caught_and_bowled") &&
      wicket.crossed === true;

    const survivorAtStrikersEnd = catchCrossedRule
      ? true
      : wicket.end === "non_strikers";

    acc.striker = survivorAtStrikersEnd ? survivor : null;
    acc.nonStriker = survivorAtStrikersEnd ? null : survivor;
  }

  // The second pass. `ledger` is already resolved, so from here on nothing needs to
  // know that corrections exist — every entry simply applies where it says it does.
  for (const entry of ledger) {
    const event = entry.event;

    if (isDelivery(event)) {
      const acc = currentInnings();
      if (acc === undefined) {
        // A delivery with no innings to belong to. Loud rather than silently dropped,
        // per PRD §7.2's "a gap is a visible error, not a silent hole".
        flag(entry, "delivery arrived before any InningsStart");
        continue;
      }
      applyDelivery(acc, entry, event);
      checkTermination(acc);
      continue;
    }

    switch (event.type) {
      case "InningsStart": {
        // Penalties awarded against the fielding side before their innings began are
        // already on the books; the innings opens with them on the board.
        const carried = pendingPenalties.get(event.inningsNo) ?? 0;
        pendingPenalties.delete(event.inningsNo);
        innings.push({
          inningsNo: event.inningsNo,
          battingTeamId: event.battingTeamId,
          bowlingTeamId: event.bowlingTeamId,
          oversAvailable: event.oversAvailable,
          target: event.target,
          isSuperOver: event.isSuperOver,
          followOn: event.followOn,
          runs: carried,
          wickets: 0,
          legalBalls: 0,
          extras: { wide: 0, noball: 0, bye: 0, legbye: 0, penalty: carried },
          batters: new Map(),
          battingOrderNext: 1,
          bowlers: new Map(),
          striker: null,
          nonStriker: null,
          currentBowler: null,
          previousOverBowler: null,
          overRunsCharged: 0,
          overBowlerWickets: 0,
          freeHit: false,
          interrupted: false,
          ended: null,
          fallOfWickets: [],
          partnerships: [],
          deliveries: [],
          penaltyBefore: carried,
          oversWereRevised: false,
          battingEdits: new Map(),
          bowlingEdits: new Map(),
          lastCorrections: [],
        });
        break;
      }

      case "InningsEnd": {
        const acc = currentInnings();
        if (acc) acc.ended = event.reason;
        break;
      }

      case "BatterIn": {
        const acc = currentInnings();
        if (!acc) break;
        const incoming = batterOf(acc, event.playerId);
        // Only a value that is one of the two. The server never rejects a ball (ADR-0011), so
        // what is read back is whatever a client sent, and a hand that is not a hand is unknown.
        if (isBatterHand(event.battingHand)) incoming.hand = event.battingHand;

        // A batter coming back from a retirement resumes his innings, so the marker
        // clears. It has to: `retired` is a *current* status and `formatBatterStatus`
        // reads it ahead of any dismissal, so leaving it set would render a batter who
        // returned and was then bowled as "retired hurt" — and would also hold the
        // all-out ceiling a wicket too low for the rest of the innings.
        if (incoming.retired === "hurt" || incoming.retired === "not_out") {
          if (!ruleSet.retiredReturnsAllowed) {
            flag(entry, "this RuleSet does not allow a retired batter to return");
          }
          incoming.retired = null;
        } else if (incoming.retired === "out") {
          // Retiring out is a wicket and it is final. Flagged rather than silently
          // normalised, because the alternative readings — un-retire him, or seat a
          // dismissed batter — are both worse than telling a human to look.
          flag(entry, "a batter who retired out cannot return");
        }

        if (event.takesStrike) {
          if (acc.striker !== null && acc.nonStriker === null) acc.nonStriker = acc.striker;
          acc.striker = event.playerId;
        } else {
          if (acc.nonStriker !== null && acc.striker === null) acc.striker = acc.nonStriker;
          acc.nonStriker = event.playerId;
        }
        if (acc.striker !== null && acc.nonStriker !== null) {
          const open = acc.partnerships[acc.partnerships.length - 1];
          if (open === undefined || !open.unbroken) openPartnership(acc);
        }
        break;
      }

      case "BatterRetired": {
        const acc = currentInnings();
        if (!acc) break;
        const batter = batterOf(acc, event.playerId);
        batter.retired = event.reason;
        // Retired out is a wicket; retired hurt and not-out are not, and must not push
        // the innings towards all-out.
        if (event.reason === "out") acc.wickets += 1;
        if (acc.striker === event.playerId) acc.striker = null;
        if (acc.nonStriker === event.playerId) acc.nonStriker = null;
        checkTermination(acc);
        break;
      }

      case "NewOver": {
        const acc = currentInnings();
        if (!acc) break;
        checkBowlerLimits(acc, event.bowlerId, true, event.overrideReason);
        acc.currentBowler = event.bowlerId;
        noteBowlingStyle(bowlerOf(acc, event.bowlerId), event);
        noteBowlerEdit(acc, entry);
        break;
      }

      case "BowlerChange": {
        const acc = currentInnings();
        if (!acc) break;
        // Same limits apply however the bowler got there — a change mid-over is still a
        // bowler taking the ball.
        checkBowlerLimits(acc, event.bowlerId, false, event.overrideReason);
        acc.currentBowler = event.bowlerId;
        noteBowlingStyle(bowlerOf(acc, event.bowlerId), event);
        noteBowlerEdit(acc, entry);
        break;
      }

      case "Interruption": {
        const acc = currentInnings();
        if (acc) acc.interrupted = true;
        break;
      }

      case "PlayResumed": {
        const acc = currentInnings();
        if (acc) acc.interrupted = false;
        break;
      }

      case "OversRevised": {
        const acc = currentInnings();
        if (!acc) break;
        acc.oversAvailable = event.revisedOvers;
        // A manual target override wins outright. `method: "manual"` exists precisely so a
        // competition can impose a number by agreement, and second-guessing it here would
        // make the engine the authority over the humans running the match (PRD §4.9).
        if (event.revisedTarget !== null) acc.target = event.revisedTarget;
        acc.oversWereRevised = true;
        // Cutting the allocation can end the innings on the spot: twelve overs already
        // bowled and the allocation revised down to twelve means the innings is over, and
        // there is no further ball coming to trigger the check.
        checkTermination(acc);
        break;
      }

      case "PenaltyRuns": {
        // `creditToInningsNo` is obeyed literally and never inferred from `awardedTo`.
        // The event carries it precisely so that two copies of the engine cannot reach
        // different conclusions about which total five runs join — and in a four-innings
        // match "the fielding side" has two innings, so `awardedTo` alone is ambiguous.
        const target = innings.find((i) => i.inningsNo === event.creditToInningsNo);
        if (target === undefined) {
          pendingPenalties.set(
            event.creditToInningsNo,
            (pendingPenalties.get(event.creditToInningsNo) ?? 0) + event.runs,
          );
          break;
        }
        target.runs += event.runs;
        // Penalties show in the extras line as `p 5` (Appendix A). They are nobody's
        // delivery, so no bowler is charged and no batter is credited.
        target.extras.penalty += event.runs;
        checkTermination(target);
        break;
      }

      case "MatchCompleted": {
        status = "completed";
        break;
      }

      /**
       * Leaving `completed` is a ceremony, and this event is its audit trail
       * (ADR-0005). Folding it puts the match back to `live`, which is what makes
       * "while live, corrections need no ceremony" true — the friction is the gate on
       * the way out, not a permanent state.
       */
      case "MatchReopened": {
        status = "live";
        break;
      }

      default:
        // DeadBall and ScorerHandoff carry no consequence for any cricket number: a dead
        // ball is recorded by *voiding* the delivery it invalidated, and a handoff is an
        // audit fact about devices. Both stay in the ledger and neither moves the fold.
        //
        // `Correction` reaches here having already done its work. It is the cross-event
        // amendment — "this was actually a different player" from a chosen point — and like
        // a supersede it cannot apply where it sits, so `substituted()` resolves it in pass
        // one and the fold sees a ledger with the right names already in it. There is
        // deliberately nothing to do at its own address.
        break;
    }
  }

  return project(
    matchId,
    ruleSet,
    innings,
    warnings,
    anomalies,
    status,
    highWaterMark(events),
    matchEditMarker(events),
    // Computed here, from the *raw* ledger, and passed in — `project` folds the
    // effective ledger, where the evidence this needs has already been resolved away.
    wasResultAmended(events),
  );
}

/**
 * Was a *published* result changed after publication?
 *
 * This is the one question the effective ledger cannot answer. A correction applies at
 * the position of the event it supersedes (ADR-0004), so by the time the fold sees it the
 * correcting event's own address — the only evidence of *when* it was written — has been
 * resolved away. So this reads the raw ledger, which is why it is a separate pass.
 *
 * "Amended" requires both halves of ADR-0005's ceremony: a `MatchReopened`, and a
 * correction written after it. A reopen alone is not an amendment — a scorer may reopen,
 * look, and change nothing — and saying "result amended" then would cast doubt on a
 * result nobody touched, which is the opposite of what the affordance is for.
 */
function wasResultAmended(events: readonly Event[]): boolean {
  const reopens = events.filter((event) => event.type === "MatchReopened");
  if (reopens.length === 0) return false;

  const after = (a: Event, b: Event): boolean =>
    a.inningsNo > b.inningsNo || (a.inningsNo === b.inningsNo && a.seq > b.seq);

  return events.some(
    (event) =>
      (event.supersedes !== null || event.voided) &&
      reopens.some((reopen) => after(event, reopen)),
  );
}

/**
 * The highest address in the **raw** ledger.
 *
 * Raw, not effective: the job is staleness detection against what has been appended
 * (ADR-0002), and a correction is the newest thing appended even though it folds at an
 * older address. Reporting its fold position would leave a freshly-corrected match
 * looking permanently stale.
 *
 * One case it cannot express, because the type is a single position: a correction to
 * innings 1 appended while innings 2 is live takes innings 1's next `seq` and so never
 * moves this mark. Detecting that needs a per-innings mark, which is a summaries problem
 * (#7) rather than an engine one.
 */
function highWaterMark(events: readonly Event[]): LedgerPosition {
  return events.reduce<LedgerPosition>(
    (highest, event) => (compareAddress(event, highest) > 0 ? addressOf(event) : highest),
    { inningsNo: 1, seq: 0 },
  );
}

function summariseDismissal(wicket: Wicket, delivery: Delivery): DismissalSummary {
  const keeperId = delivery.keeperId;
  const fielders: DismissalFielder[] = wicket.fielderIds.map((playerId) => ({
    playerId,
    isKeeper: keeperId !== null && playerId === keeperId,
  }));

  // A bowler is only named in the notation for dismissals he is credited with. A run
  // out reads `run out (Verma/†Khan)` with no bowler at all.
  const bowlerId = WICKETS_TO_BOWLER.has(wicket.type) ? delivery.bowlerId : null;

  return { type: wicket.type, bowlerId, fielders };
}

// ── Projection ──────────────────────────────────────────────────────────────

function project(
  matchId: MatchId,
  ruleSet: RuleSet,
  accs: readonly InningsAcc[],
  warnings: readonly RuleWarning[],
  anomalies: readonly AnomalyRef[],
  status: MatchStatus,
  computedThrough: LedgerPosition,
  edited: EditMarker | null,
  resultAmended: boolean,
): MatchState {
  const innings = accs.map((acc): InningsState => {
    const extras: ExtrasBreakdown = {
      ...acc.extras,
      total:
        acc.extras.wide +
        acc.extras.noball +
        acc.extras.bye +
        acc.extras.legbye +
        acc.extras.penalty,
    };

    const batting = [...acc.batters.values()]
      .sort((a, b) => a.battingOrder - b.battingOrder)
      .map(
        (b): BattingLine => ({
          playerId: b.playerId,
          batted: b.batted,
          runs: b.runs,
          balls: b.balls,
          fours: b.fours,
          sixes: b.sixes,
          dots: b.dots,
          strikeRate: b.balls === 0 ? null : (b.runs / b.balls) * 100,
          dismissal: b.dismissal,
          edited: markerFor(acc.battingEdits, b.playerId),
          retired: b.retired,
          canReturn: b.retired === "hurt" || b.retired === "not_out",
          isStriker: acc.striker === b.playerId,
          isNonStriker: acc.nonStriker === b.playerId,
          battingOrder: b.battingOrder,
          hand: b.hand,
        }),
      );

    const bowling = [...acc.bowlers.values()].map(
      (b): BowlingLine => ({
        playerId: b.playerId,
        overs: overCount(b.legalBalls, ruleSet.ballsPerOver),
        maidens: b.maidens,
        wicketMaidens: b.wicketMaidens,
        runsConceded: b.runsConceded,
        wickets: b.wickets,
        economy:
          b.legalBalls === 0 ? null : b.runsConceded / (b.legalBalls / ruleSet.ballsPerOver),
        wides: b.wides,
        noBalls: b.noBalls,
        dots: b.dots,
        isCurrentBowler: acc.currentBowler === b.playerId,
        edited: markerFor(acc.bowlingEdits, b.playerId),
        arm: b.arm,
        type: b.type,
      }),
    );

    const partnerships = acc.partnerships.map(
      (p): Partnership => ({
        runs: p.runs,
        balls: p.balls,
        playerAId: p.a,
        playerBId: p.b,
        unbroken: p.unbroken,
      }),
    );

    return {
      inningsNo: acc.inningsNo,
      battingTeamId: acc.battingTeamId,
      bowlingTeamId: acc.bowlingTeamId,
      runs: acc.runs,
      wickets: acc.wickets,
      overs: overCount(acc.legalBalls, ruleSet.ballsPerOver),
      oversAvailable: acc.oversAvailable ?? ruleSet.overs,
      extras,
      allOut: acc.ended === "all_out",
      batting,
      bowling,
      fallOfWickets: acc.fallOfWickets,
      partnerships,
      deliveries: acc.deliveries,
      target: acc.target,
      ended: acc.ended,
      interrupted: acc.interrupted,
      isSuperOver: acc.isSuperOver,
      followOn: acc.followOn,
    };
  });

  const current = accs[accs.length - 1];
  const currentState = innings[innings.length - 1];

  return {
    matchId,
    status,
    ruleSet,
    innings,
    currentInningsNo: current?.inningsNo ?? null,
    header:
      current === undefined || currentState === undefined
        ? null
        : buildHeader(ruleSet, current, currentState),
    result: buildResult(ruleSet, innings, status),
    warnings,
    anomalies,
    computedThrough,
    edited,
    resultAmended,
  };
}

/**
 * The par score: what the chasing side should have after the balls they have faced.
 *
 * **The formula in full, because "transparent and published" is the requirement (PRD
 * §6.6):** the side batting first scored `target - 1`. Par after `B` of the `N` balls now
 * available is that total shared out pro rata — `floor((target - 1) × B / N)`. Level with
 * par is a tie; one ahead is winning.
 *
 * **This is not DLS and must never be labelled as such.** DLS is licensed intellectual
 * property and it models *resources* rather than balls, so it gives different — better —
 * answers. Presenting this as DLS would be a legal exposure and, the first time a captain
 * checked it against the real thing, a credibility one. It is an average-run-rate par,
 * which is exactly what `OversRevised.method` says it is.
 *
 * Null until the overs have actually been cut: in an untouched chase this is just the run
 * rate in a more official-looking hat, and showing it would imply a rain rule is in play
 * when none is.
 */
function parScoreFor(ruleSet: RuleSet, acc: InningsAcc, state: InningsState): number | null {
  if (!acc.oversWereRevised || acc.target === null) return null;
  const available = state.oversAvailable;
  if (available === null) return null;
  const totalBalls = available * ruleSet.ballsPerOver;
  if (totalBalls === 0) return null;
  const ballsFaced = Math.min(acc.legalBalls, totalBalls);
  return Math.floor(((acc.target - 1) * ballsFaced) / totalBalls);
}

function buildHeader(ruleSet: RuleSet, acc: InningsAcc, state: InningsState): ScorerHeader {
  const available = state.oversAvailable;
  const totalBalls = available === null ? null : available * ruleSet.ballsPerOver;
  const ballsRemaining = totalBalls === null ? null : Math.max(0, totalBalls - acc.legalBalls);
  const runsRequired = acc.target === null ? null : Math.max(0, acc.target - acc.runs);

  const byId = (playerId: PlayerId | null): BattingLine | null =>
    playerId === null ? null : (state.batting.find((b) => b.playerId === playerId) ?? null);

  const currentOverNo = Math.floor(acc.legalBalls / ruleSet.ballsPerOver);

  return {
    runs: acc.runs,
    wickets: acc.wickets,
    overs: state.overs,
    currentRunRate:
      acc.legalBalls === 0 ? null : acc.runs / (acc.legalBalls / ruleSet.ballsPerOver),
    requiredRunRate:
      runsRequired === null || ballsRemaining === null || ballsRemaining === 0
        ? null
        : runsRequired / (ballsRemaining / ruleSet.ballsPerOver),
    target: acc.target,
    runsRequired,
    ballsRemaining,
    parScore: parScoreFor(ruleSet, acc, state),
    lead: null,
    striker: byId(acc.striker),
    nonStriker: byId(acc.nonStriker),
    bowler: state.bowling.find((b) => b.isCurrentBowler) ?? null,
    currentPartnership: state.partnerships.find((p) => p.unbroken) ?? null,
    freeHit: acc.freeHit,
    currentOver: state.deliveries.filter((d) => d.over === currentOverNo),
  };
}

/**
 * `1 run`, `24 runs`, `1 ball`, `5 balls`.
 *
 * A result is the single most-read string the product produces — it is the headline on
 * the public page and the text that gets screenshotted into a WhatsApp group. "won by 1
 * runs" is precisely the absurd-but-telling detail bug the PRD calls out incumbents for,
 * and it is the cheapest possible thing to get right.
 */
const plural = (count: number, noun: string): string =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

/**
 * A result that ended the match before the cricket did.
 *
 * Checked ahead of everything else, because none of them need a completed innings: a side
 * that forfeits has not batted, and a washed-out match has no second innings to compare.
 * The three are kept apart deliberately — `ResultKind` distinguishes them and a points
 * table pays out differently for each, so collapsing any pair misreports a standings
 * position. Nobody's fault (`abandoned`) is not the same as declining to play
 * (`forfeited`) is not the same as walking off half way (`conceded`).
 */
function buildNonCricketResult(innings: readonly InningsState[]): MatchResult | null {
  const surrendered = innings.find(
    (i) => i.ended === "forfeited" || i.ended === "conceded",
  );
  if (surrendered !== undefined) {
    const kind = surrendered.ended === "forfeited" ? "forfeited" : "conceded";
    return {
      kind,
      // The side whose *innings* ended this way is the side that lost it, so the winner
      // is whoever was bowling at them.
      winnerTeamId: surrendered.bowlingTeamId,
      margin: null,
      ballsRemaining: null,
      decidedBySuperOver: false,
      text: kind === "forfeited" ? "won by forfeit" : "won after the opposition conceded",
    };
  }

  if (innings.some((i) => i.ended === "abandoned")) {
    return {
      kind: "abandoned",
      winnerTeamId: null,
      margin: null,
      ballsRemaining: null,
      decidedBySuperOver: false,
      text: "match abandoned",
    };
  }

  return null;
}

/** The margin of a completed chase, from the chasing side's point of view. */
function chaseMargin(
  ruleSet: RuleSet,
  second: InningsState,
): { readonly wicketsLeft: number; readonly ballsRemaining: number | null } {
  // Wickets in hand has nothing to do with whether the format has an over limit.
  const wicketsLeft = wicketsThatEndInnings(ruleSet) - second.wickets;
  const totalBalls =
    second.oversAvailable === null ? null : second.oversAvailable * ruleSet.ballsPerOver;
  const ballsFaced = second.overs.completed * ruleSet.ballsPerOver + second.overs.balls;
  return {
    wicketsLeft,
    ballsRemaining: totalBalls === null ? null : Math.max(0, totalBalls - ballsFaced),
  };
}

/** Who won a completed pair of innings, or null if the pair was tied. */
function decidePair(
  ruleSet: RuleSet,
  first: InningsState,
  second: InningsState,
  superOver: boolean,
): MatchResult | null {
  /*
   * The number that actually decides the match.
   *
   * A revised target *replaces* the first innings' total for this purpose — that is what
   * revising it means. Comparing raw totals instead hands the match to whoever scored more
   * while the other side passed the target it was actually set, which is exactly what rain
   * produces: 6 defended, the chase cut and the target moved to 4, and the side on 4 have
   * won despite trailing on runs.
   */
  const targetToBeat = second.target ?? first.runs + 1;
  const defendingTotal = targetToBeat - 1;

  if (second.runs >= targetToBeat) {
    const { wicketsLeft, ballsRemaining } = chaseMargin(ruleSet, second);
    return {
      kind: "wickets",
      winnerTeamId: second.battingTeamId,
      margin: wicketsLeft,
      ballsRemaining,
      decidedBySuperOver: superOver,
      // A super over is reported as one, not as "won by 2 wickets (4 balls remaining)" —
      // which is true but reads as a normal chase and buries the thing that happened.
      text: superOver
        ? "won the super over"
        : ballsRemaining === null
          ? `won by ${plural(wicketsLeft, "wicket")}`
          : `won by ${plural(wicketsLeft, "wicket")} (${plural(ballsRemaining, "ball")} remaining)`,
    };
  }

  if (defendingTotal > second.runs) {
    const margin = defendingTotal - second.runs;
    return {
      kind: "runs",
      winnerTeamId: first.battingTeamId,
      margin,
      ballsRemaining: null,
      decidedBySuperOver: superOver,
      text: superOver ? "won the super over" : `won by ${plural(margin, "run")}`,
    };
  }

  return null;
}

/**
 * The result of the match.
 *
 * Innings come in pairs: the match itself is the first pair, and a tie sends it to a
 * super over which is the next pair — and a tied super over sends it to another, which is
 * why this iterates rather than special-casing innings 3 and 4. A super over lives at
 * innings 5 and 6 the second time round because `seq` is unique per
 * `(match_id, innings_no, seq)`, so reusing 3 and 4 would collide with the first one.
 *
 * Multi-innings formats are handled as one pair per side's completed innings, with a
 * `draw` when the overs ran out with the match undecided — distinct from `no_result`,
 * which is a washout. Conflating them misreports both the standings and the public page.
 */
function buildResult(
  ruleSet: RuleSet,
  innings: readonly InningsState[],
  status: MatchStatus,
): MatchResult | null {
  const surrendered = buildNonCricketResult(innings);
  if (surrendered !== null) return surrendered;

  if (innings.length < 2) {
    // A match closed with only one innings played and no forfeit produced no cricket to
    // compare — a washout, which is not the same as a draw.
    if (status === "completed") {
      return {
        kind: "no_result",
        winnerTeamId: null,
        margin: null,
        ballsRemaining: null,
        decidedBySuperOver: false,
        text: "no result",
      };
    }
    return null;
  }

  if (ruleSet.inningsPerSide !== 1) return buildMultiInningsResult(ruleSet, innings, status);

  // Walk the pairs newest-last, so the deciding pair is the last one that finished.
  let decided: MatchResult | null = null;
  let pairs = 0;
  for (let index = 0; index + 1 < innings.length; index += 2) {
    const first = innings[index] as InningsState;
    const second = innings[index + 1] as InningsState;
    if (first.ended === null || second.ended === null) break;
    pairs += 1;
    // `pairs > 1` rather than `second.isSuperOver`, so a fixture that forgets the flag
    // still cannot be reported as a normal win — the position in the match is the fact.
    decided = decidePair(ruleSet, first, second, pairs > 1);
    if (decided !== null) return decided;
  }

  if (pairs === 0) return null;

  // Every completed pair was level. If the RuleSet plays a super over, another one is
  // due and the match is not over; otherwise the tie stands.
  if (ruleSet.superOverOnTie && status !== "completed") return null;
  return {
    kind: "tie",
    winnerTeamId: null,
    margin: null,
    ballsRemaining: null,
    decidedBySuperOver: false,
    text: "match tied",
  };
}

/**
 * Two innings a side: a win by runs or wickets, an innings victory, or a draw.
 *
 * An innings victory is the case worth naming — a side that wins without needing its
 * second innings wins "by an innings and N runs", and reporting it as a plain runs margin
 * loses the distinction every scorecard in the sport makes.
 */
function buildMultiInningsResult(
  ruleSet: RuleSet,
  innings: readonly InningsState[],
  status: MatchStatus,
): MatchResult | null {
  const complete = innings.filter((i) => i.ended !== null);
  if (complete.length < 3) return status === "completed" ? drawnResult() : null;

  const totals = new Map<TeamId, number>();
  for (const state of complete) {
    totals.set(state.battingTeamId, (totals.get(state.battingTeamId) ?? 0) + state.runs);
  }
  const sides = [...totals.entries()];
  const [sideA, sideB] = sides;
  if (sideA === undefined || sideB === undefined) return null;

  const [leaderId, leaderRuns] = sideA[1] >= sideB[1] ? sideA : sideB;
  const [trailerId, trailerRuns] = sideA[1] >= sideB[1] ? sideB : sideA;
  void trailerId;
  const margin = leaderRuns - trailerRuns;

  // The chasing side has been dismissed or has run out of innings.
  const trailerInningsPlayed = complete.filter((i) => i.battingTeamId !== leaderId).length;
  const leaderInningsPlayed = complete.filter((i) => i.battingTeamId === leaderId).length;
  const decided = trailerInningsPlayed >= 2 && margin > 0;

  if (!decided) return status === "completed" ? drawnResult() : null;

  if (leaderInningsPlayed === 1) {
    return {
      kind: "innings",
      winnerTeamId: leaderId,
      margin,
      ballsRemaining: null,
      decidedBySuperOver: false,
      text: `won by an innings and ${plural(margin, "run")}`,
    };
  }

  const chasedDown = complete[complete.length - 1]?.ended === "target_reached";
  if (chasedDown) {
    const last = complete[complete.length - 1] as InningsState;
    const { wicketsLeft } = chaseMargin(ruleSet, last);
    return {
      kind: "wickets",
      winnerTeamId: last.battingTeamId,
      margin: wicketsLeft,
      ballsRemaining: null,
      decidedBySuperOver: false,
      text: `won by ${plural(wicketsLeft, "wicket")}`,
    };
  }

  return {
    kind: "runs",
    winnerTeamId: leaderId,
    margin,
    ballsRemaining: null,
    decidedBySuperOver: false,
    text: `won by ${plural(margin, "run")}`,
  };
}

/** A multi-day match that ran out of time. Never a washout — that is `no_result`. */
const drawnResult = (): MatchResult => ({
  kind: "draw",
  winnerTeamId: null,
  margin: null,
  ballsRemaining: null,
  decidedBySuperOver: false,
  text: "match drawn",
});
